import { Check, Lock } from 'lucide-react'
import { Button } from '@shared/design-system/ui'
import { checkoutUrlForMember } from '../lib/checkout-member-url'
import {
  muted,
  offerBody,
  offerChip,
  offerHero,
  offerHeroBadge,
  offerHeroContent,
  offerHeroImage,
  offerHeroMeta,
  offerHeroShade,
  offerHeroTitle,
  offerLayout,
  offerLead,
  offerModule,
  offerModuleCopy,
  offerModuleIndex,
  offerModuleTitle,
  offerModules,
  offerPage,
  offerPanel,
  offerPerk,
  offerPerkIcon,
  offerPerks,
  offerPrice,
  offerSection,
  offerSectionTitle,
} from './member-access.css'

export type CourseOfferView = {
  name: string
  coverImage: string
  description: string
  modules: readonly { id: string; title: string; lessonCount: number }[]
  priceLabel: string | null
  checkoutUrl: string | null
}

const PERKS = [
  'Desbloqueia na hora, depois do pagamento',
  'Entra na mesma conta deste e-mail',
  'Assista quando quiser no portal',
] as const

function lessonLabel(count: number) {
  return `${count} ${count === 1 ? 'aula' : 'aulas'}`
}

function moduleLabel(count: number) {
  return `${count} ${count === 1 ? 'módulo' : 'módulos'}`
}

export function MemberCourseOffer({
  offer,
  memberEmail,
}: {
  offer: CourseOfferView
  memberEmail?: string | null
}) {
  const lessons = offer.modules.reduce((sum, module) => sum + module.lessonCount, 0)
  const checkoutHref = offer.checkoutUrl ? checkoutUrlForMember(offer.checkoutUrl, memberEmail) : null
  return (
    <div className={offerPage}>
      <section className={offerHero}>
        {offer.coverImage ? <img className={offerHeroImage} src={offer.coverImage} alt="" /> : null}
        <span className={offerHeroShade} />
        <div className={offerHeroContent}>
          <span className={offerHeroBadge}>Bloqueado</span>
          <h1 className={offerHeroTitle}>{offer.name}</h1>
          <p className={offerHeroMeta}>
            <span className={offerChip}>{moduleLabel(offer.modules.length)}</span>
            <span className={offerChip}>{lessonLabel(lessons)}</span>
          </p>
        </div>
      </section>
      <div className={offerLayout}>
        <div className={offerBody}>
          {offer.description ? (
            <section className={offerSection}>
              <h2 className={offerSectionTitle}>Sobre o curso</h2>
              <p className={offerLead}>{offer.description}</p>
            </section>
          ) : null}
          {offer.modules.length > 0 ? (
            <section className={offerSection}>
              <h2 className={offerSectionTitle}>Conteúdo</h2>
              <ol className={offerModules}>
                {offer.modules.map((module, index) => (
                  <li key={module.id} className={offerModule}>
                    <span className={offerModuleIndex}>{String(index + 1).padStart(2, '0')}</span>
                    <span className={offerModuleCopy}>
                      <span className={offerModuleTitle}>{module.title}</span>
                      <span className={muted}>{lessonLabel(module.lessonCount)}</span>
                    </span>
                    <Lock size={16} aria-hidden />
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
        <aside className={offerPanel}>
          {offer.priceLabel ? <p className={offerPrice}>{offer.priceLabel}</p> : <p className={muted}>Curso bloqueado</p>}
          <ul className={offerPerks}>
            {PERKS.map((perk) => (
              <li key={perk} className={offerPerk}>
                <span className={offerPerkIcon} aria-hidden>
                  <Check size={14} strokeWidth={2.6} />
                </span>
                {perk}
              </li>
            ))}
          </ul>
          {checkoutHref ? (
            <a href={checkoutHref} target="_blank" rel="noopener noreferrer">
              <Button fullWidth>Comprar</Button>
            </a>
          ) : (
            <Button fullWidth disabled>
              Indisponível
            </Button>
          )}
        </aside>
      </div>
    </div>
  )
}
